import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";
import { sseFetch } from "../../api/sseFetch";
import { APP_RESUMED_EVENT } from "../../api/appLifecycle";

export const useNotifications = (options = {}) =>
  useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      apiFetch("/notifications", { query: { sort: "created_at", order: "desc", pageSize: 30 } }),
    select: (data) => data.items || [],
    ...options,
  });

/**
 * Live delivery (Flujo Contacto #1). Opens GET /notifications/stream and
 * refreshes the cache whenever the server pushes.
 *
 * Mount ONCE, high in the tree — one connection per session, not per component.
 *
 * Invalidates rather than splicing the pushed row into the cache: the row is
 * already durable server-side, and a refetch keeps ordering, paging and the
 * unread count consistent with everything else instead of hand-maintaining them.
 *
 * Reconnects with a backoff, because a dropped stream is a normal thing (phone
 * sleeps, wifi flips, proxy reaps). The list still polls on mount, so a gap
 * degrades to "not instant", never to "lost".
 */
export const useNotificationStream = (enabled = true) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let retryMs = 1000;
    let timer = null;
    let controller = null;

    const connect = async () => {
      controller = new AbortController();
      const { signal } = controller;
      try {
        await sseFetch("/notifications/stream", {
          method: "GET",
          signal,
          onEvent: (event) => {
            if (event !== "notification") return; // ready / ping
            retryMs = 1000; // a delivered event proves the link is healthy
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            queryClient.invalidateQueries({ queryKey: ["contact-requests"] });
            queryClient.invalidateQueries({ queryKey: ["direct-messages"] });
            // Academy join requests / confirmations change the roster and the
            // athlete's membership card.
            queryClient.invalidateQueries({ queryKey: ["academy-athletes"] });
            queryClient.invalidateQueries({ queryKey: ["my-athletes"] });
          },
        });
      } catch (error) {
        if (cancelled || error?.name === "AbortError") return;
        // 401 already cleared the token and broadcast SIGNED_OUT_EVENT; there is
        // nothing to reconnect to.
        if (error?.status === 401) return;
      }
      if (cancelled || signal.aborted) return;
      // The stream also ends normally (server restart, proxy idle cut) — same
      // handling either way.
      timer = setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, 30000);
    };

    // Back from the background: the old socket is most likely dead. Drop it and
    // reconnect now rather than waiting out the backoff.
    const reconnectNow = () => {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      controller?.abort();
      retryMs = 1000;
      connect();
    };

    connect();
    window.addEventListener(APP_RESUMED_EVENT, reconnectNow);

    return () => {
      cancelled = true;
      window.removeEventListener(APP_RESUMED_EVENT, reconnectNow);
      if (timer) clearTimeout(timer);
      controller?.abort();
    };
  }, [enabled, queryClient]);
};

export const useMarkNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }) =>
      apiFetch("/notifications/mark-read", { method: "POST", body: { ids } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch("/notifications/read-all", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
};
